import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

export interface CarnetFile {
  path: string;
  name?: string;
}

export interface CarnetItem {
  id: number;
  name: string;
  description?: string;
  trimestre: 'premiere' | 'deuxieme' | 'troisieme' | 'annexe';
  type: 'carnet_prive' | 'carnet_etatique' | 'certificat_prive' | 'certificat_etatique' | 'annexe';
  files: CarnetFile[];
  vu: string;
  creationDate: string;
  // Optional metadata returned by the API that we reuse for showCarnet
  version?: string;
  Version?: string;
  typeDocument?: string;
  Type?: string;
  session?: string | number;
  Session?: string | number;
  sessionvl?: string | number;
  idOrg?: number;
  idorg?: number;
  idGuardian?: number;
  idguardian?: number;
  carnet?: string;
  carnetvl?: string;
  carnetv1?: string;
  carnetv2?: string;
  certifvl?: string;
  certificat?: string;
  certificatv1?: string;
  certificatv2?: string;
  bulletin?: string;
  bulletinvl?: string;
  annexe?: string;
  annexevl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CarnetService {
  private readonly bulletinUrl =
    'https://mon-compte.educanet.pro/mobile/bulletinmanazahios/getbulletin';
  private readonly showCarnetBaseUrl =
    'https://mon-compte.educanet.pro/mobile/carnet/showCarnet';

  constructor(private http: HttpClient) { }

  getStudentCarnets(
    nameOrg: string,
    gradeId: number,
    batchId: number,
    idStudent: number,
    options?: {
      idOrg?: number;
      idGuardian?: number;
      version?: string;
      session?: string;
      type?: string;
    }
  ): Observable<CarnetItem[]> {
    const bulletinEndpoint = `${this.bulletinUrl}/${idStudent}`;
    return this.http.get<any>(bulletinEndpoint).pipe(
      switchMap((bulletinResponse) => {
        const derivedParams = this.deriveShowCarnetParams(
          bulletinResponse,
          {
            idStudent,
            gradeId,
            batchId,
            idOrg: options?.idOrg,
            idGuardian: options?.idGuardian,
            nameOrg
          },
          options
        );

        if (!derivedParams) {
          return of(this.normalizeResponse(bulletinResponse));
        }

        const showCarnetUrl = `${this.showCarnetBaseUrl}/${derivedParams.version}/${derivedParams.gradeId}/${derivedParams.batchId}/${derivedParams.session}/${derivedParams.idStudent}/${derivedParams.idOrg}/${derivedParams.idGuardian}/${derivedParams.type}/${derivedParams.encodedNameOrg}`;


        return this.http.get<any>(showCarnetUrl).pipe(
          map((showCarnetResponse) => this.normalizeResponse(showCarnetResponse)),
          catchError((showCarnetError) => {
            console.error(
              '[CarnetService] ❌ Error calling showCarnet. Falling back to getbulletin response.',
              showCarnetError
            );
            return of(this.normalizeResponse(bulletinResponse));
          })
        );
      }),
      catchError((error) => {
        console.error(
          '[CarnetService] ❌ Error fetching carnets for student ID:',
          idStudent,
          error
        );
        return throwError(() => error);
      })
    );
  }

  getCarnetHtml(params: {
    version: string;
    gradeId: number;
    batchId: number;
    session: string | number;
    idStudent: number;
    idOrg: number;
    idGuardian: number;
    type: string;
    nameOrg: string;
  }): Observable<string> {
    const url = `${this.showCarnetBaseUrl}/${params.version}/${params.gradeId}/${params.batchId}/${params.session}/${params.idStudent}/${params.nameOrg}/${params.idGuardian}/${params.type}/${params.idOrg}`;

    return this.http
      .get(url, { responseType: 'text' })
      .pipe(
        map((html) => this.decodeHtmlResponse(html)),
        catchError((error) => {
          console.error('[CarnetService] ❌ Error fetching showCarnet HTML:', error);
          return throwError(() => error);
        })
      );
  }

  private normalizeResponse(response: any): CarnetItem[] {
    if (!response) {
      return [];
    }
    let raw: any[] = [];
    if (Array.isArray(response)) {
      raw = response;
    } else if (Array.isArray(response?.liste)) {
      raw = response.liste;
    } else {
      raw = [response];
    }
    return this.mapCarnetItems(raw);
  }

  private deriveShowCarnetParams(
    bulletinResponse: any,
    base: {
      idStudent: number;
      gradeId: number;
      batchId: number;
      idOrg?: number;
      idGuardian?: number;
      nameOrg: string;
    },
    explicit?: {
      version?: string;
      session?: string;
      type?: string;
      idOrg?: number;
      idGuardian?: number;
    }
  ):
    | {
        version: string;
        session: string;
        type: string;
        idStudent: number;
        gradeId: number;
        batchId: number;
        idOrg: number;
        idGuardian: number;
        encodedNameOrg: string;
      }
    | null {
    const firstEntry = this.extractFirstEntry(bulletinResponse);
    
    // Try to extract from multiple items if array
    let version: string | null = null;
    let session: string | null = null;
    let type: string | null = null;
    
    if (Array.isArray(bulletinResponse) && bulletinResponse.length > 0) {
      // Try first few items to find version/type
      for (let i = 0; i < Math.min(5, bulletinResponse.length); i++) {
        const item = bulletinResponse[i];
        if (!version && (item?.version || item?.Version || item?.VERSION)) {
          version = item.version || item.Version || item.VERSION;
        }
        if (!type && (item?.type || item?.Type || item?.TYPE)) {
          type = item.type || item.Type || item.TYPE;
        }
        if (!session && (item?.session || item?.Session || item?.SESSION)) {
          session = item.session || item.Session || item.SESSION;
        }
      }
    }
    
    // Fallback to explicit, firstEntry, or response level
    version = explicit?.version || version || firstEntry?.version || firstEntry?.Version || bulletinResponse?.version || null;
    session = explicit?.session || session || firstEntry?.session || firstEntry?.Session || bulletinResponse?.session || null;
    type = explicit?.type || type || firstEntry?.type || firstEntry?.Type || bulletinResponse?.type || null;
    
    const idOrg =
      explicit?.idOrg ||
      base.idOrg ||
      firstEntry?.idOrg ||
      firstEntry?.idorg ||
      firstEntry?.IdOrg ||
      bulletinResponse?.idOrg ||
      bulletinResponse?.idorg ||
      null;
    
    // Handle idGuardian: 0 is valid, only check for null/undefined
    let idGuardian: number | null = null;
    if (explicit?.idGuardian !== undefined && explicit?.idGuardian !== null) {
      idGuardian = explicit.idGuardian;
    } else if (base.idGuardian !== undefined && base.idGuardian !== null) {
      idGuardian = base.idGuardian;
    } else if (firstEntry?.idGuardian !== undefined && firstEntry?.idGuardian !== null) {
      idGuardian = firstEntry.idGuardian;
    } else if (firstEntry?.idguardian !== undefined && firstEntry?.idguardian !== null) {
      idGuardian = firstEntry.idguardian;
    } else if (bulletinResponse?.idGuardian !== undefined && bulletinResponse?.idGuardian !== null) {
      idGuardian = bulletinResponse.idGuardian;
    } else if (bulletinResponse?.idguardian !== undefined && bulletinResponse?.idguardian !== null) {
      idGuardian = bulletinResponse.idguardian;
    }
    // Check if all required params are present (idGuardian can be 0)
    if (!version || !session || idOrg === null || idOrg === undefined || idGuardian === null || idGuardian === undefined || !type) {
      return null;
    }

    return {
      version: String(version),
      session: String(session),
      type: String(type),
      idStudent: base.idStudent,
      gradeId: base.gradeId,
      batchId: base.batchId,
      idOrg: Number(idOrg),
      idGuardian: Number(idGuardian),
      encodedNameOrg: encodeURIComponent(base.nameOrg || '')
    };
  }

  private extractFirstEntry(response: any): any {
    if (!response) {
      return null;
    }
    if (Array.isArray(response)) {
      return response[0] || null;
    }
    if (Array.isArray(response?.liste)) {
      return response.liste[0] || null;
    }
    if (typeof response === 'object') {
      return response;
    }
    return null;
  }

  private decodeHtmlResponse(payload: string): string {
    if (!payload) {
      return '';
    }

    const trimmed = payload.trim();

    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      try {
        return JSON.parse(trimmed);
      } catch (error) {
        console.warn('[CarnetService] ⚠️ Unable to JSON.parse HTML payload:', error);
      }
    }

    try {
      return JSON.parse(`"${trimmed.replace(/"/g, '\\"')}"`);
    } catch {
      return trimmed
        .replace(/\\u003c/gi, '<')
        .replace(/\\u003e/gi, '>')
        .replace(/\\u0026/gi, '&')
        .replace(/\\u003d/gi, '=')
        .replace(/\\u0027/gi, "'")
        .replace(/\\u0022/gi, '"')
        .replace(/\\n/gi, '\n')
        .replace(/\\r/gi, '\r')
        .replace(/\\t/gi, '\t');
    }
  }

  private mapCarnetItems(items: any[]): CarnetItem[] {
    let idCounter = 1;
    const result: CarnetItem[] = [];
    for (const it of items) {
      const s = typeof it?.session === 'number' ? it.session : null;
      const tri = s === 0 ? 'premiere' : s === 1 ? 'deuxieme' : s === 2 ? 'troisieme' : 'annexe';
      const v1 = typeof it?.carnetv1 === 'string' ? it.carnetv1.trim() : '';
      const v2 = typeof it?.carnetv2 === 'string' ? it.carnetv2.trim() : '';
      const n1 = typeof it?.namecarnetv1 === 'string' ? it.namecarnetv1 : '';
      const n2 = typeof it?.namecarnetv2 === 'string' ? it.namecarnetv2 : '';
      if (v1) {
        result.push({
          id: idCounter++,
          name: n1 || 'Carnet étatique',
          description: '',
          trimestre: tri,
          type: 'carnet_etatique',
          files: [{ path: v1, name: n1 }],
          vu: '',
          creationDate: ''
        });
      }
      if (v2) {
        result.push({
          id: idCounter++,
          name: n2 || 'Carnet privé',
          description: '',
          trimestre: tri,
          type: 'carnet_prive',
          files: [{ path: v2, name: n2 }],
          vu: '',
          creationDate: ''
        });
      }
    }
    return result;
  }

}
